import React from 'react';
import styled from '@emotion/styled';

const Section = styled.section`
  padding: 96px 24px;
  background: #ffffff;
  position: relative;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px;
  align-items: center;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 48px;
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  aspect-ratio: 4/5;
  background: #f3f4f6;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(to top, rgba(0,0,0,0.4) 0%, transparent 50%);
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Label = styled.span`
  color: #7664c6;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const Title = styled.h2`
  font-family: 'Space Grotesk', sans-serif;
  font-size: clamp(2rem, 4vw, 2.65rem);
  font-weight: 700;
  line-height: 1.2;
  color: #0f1116;
  letter-spacing: -0.04em;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const Text = styled.p`
  font-family: 'Public Sans', sans-serif;
  font-size: 18px;
  line-height: 1.7;
  color: #666a72;
  margin: 0;
`;

const Quote = styled.blockquote`
  border-left: 3px solid #7664c6;
  padding-left: 24px;
  margin: 24px 0;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 24px;
  font-weight: 500;
  font-style: italic;
  color: #303238;
  line-height: 1.4;
`;

const Signature = styled.div`
  margin-top: 16px;
  
  strong {
    display: block;
    font-family: 'Space Grotesk', sans-serif;
    font-size: 18px;
    color: #0f1116;
  }
  
  span {
    font-family: 'Public Sans', sans-serif;
    font-size: 14px;
    color: #6b7280;
  }
`;

const FounderStory: React.FC = () => {
    return (
        <Section>
            <Container>
                <ImageWrapper>
                    <img
                        src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?ixlib=rb-4.0.3&auto=format&fit=crop&w=1632&q=80"
                        alt="SmushChat Team working together"
                    />
                </ImageWrapper>

                <Content>
                    <Label>Our Story</Label>
                    <Title>Built for founders, by founders.</Title>

                    <Text>
                        We started SmushChat because we were frustrated with the existing options.
                        Enterprise tools were too expensive and complex, while simple chatbots lacked
                        the intelligence to actually be useful.
                    </Text>

                    <Text>
                        As a team of developers and entrepreneurs, we knew there had to be a better way.
                        We built SmushChat to bridge the gap: a configurable AI concierge for founders
                        who want answers grounded in their own business knowledge.
                    </Text>

                    <Quote>
                        "We believe every business deserves AI support that feels human, without the enterprise price tag."
                    </Quote>

                    <Signature>
                        <strong>The SmushChat Team</strong>
                        <span>Founders & Developers</span>
                    </Signature>
                </Content>
            </Container>
        </Section>
    );
};

export default FounderStory;
